import {
  Box, Card, CardHeader, Skeleton,
} from '@mui/material';
import { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Chart, { useChart, ApexOptions } from '@mgm/common/components/charts';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { AnimatedDivider } from '../../../../components';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getIsOrdersLoading } from '../../../../store/orders/selectors';

type TTeamStat = {
  id: number;
  name: string;
  members: Array<{
    name: string;
    email: string;
    totalHoursWorked: number;
  }>;
};

type TTopTeamsByHoursChart = {
  teams: TTeamStat[];
};

const TopTeamsByHoursChart: FC<TTopTeamsByHoursChart> = ({ teams }) => {
  const { t } = useTranslation(['orders', 'inputs', 'common']);
  const isOrdersLoading = useAppSelector(getIsOrdersLoading);
  const chartRef = useRef<any>(null);

  const chartData = useMemo(() => {
    const sorted = teams
      .map((team) => ({
        name: team.name,
        totalHoursWorked: team.members.reduce((sum, m) => sum + (m.totalHoursWorked || 0), 0),
      }))
      .sort((a, b) => b.totalHoursWorked - a.totalHoursWorked);

    return {
      categories: sorted.map((t) => t.name),
      seriesData: sorted.map((t) => t.totalHoursWorked),
    };
  }, [teams]);

  const chartOptions: ApexOptions = useChart({
    chart: {
      type: 'bar',
      events: {
        mounted: (chart) => {
          chartRef.current = chart;
        },
      },
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        rotate: -45,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      title: { text: 'Total Hours Worked' },
      labels: {
        formatter: (value: number) => `${value.toFixed(1)}h`,
      },
    },
    tooltip: {
      shared: false,
      intersect: true,
      y: {
        formatter: (value: number) => `${value.toFixed(1)} ${t('hours', { ns: 'inputs' })}`,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '40%',
        distributed: true,
      },
    },
  });

  const validSeries = [
    {
      name: 'Total Hours Worked',
      data: chartData.seriesData,
    },
  ];

  return (
    <Card>
      <CardHeader
        sx={{ p: 2 }}
        title="Top Performing Teams by Total Hours Worked"
      />
      <AnimatedDivider loading={isOrdersLoading} />
      <Box sx={{ mx: 3 }} dir="ltr">
        {/* eslint-disable-next-line no-nested-ternary */}
        {isOrdersLoading ? (
          <Box pt={3}>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i?.toString()} variant="rectangular" height={40} sx={{ mb: 2 }} />
            ))}
          </Box>
        ) : chartData.categories.length ? (
          <Chart
            type="bar"
            series={validSeries}
            options={chartOptions}
            height={400}
          />
        ) : (
          <CustomNoRowsOverlay text={t('noDataForToday', { ns: 'common' })} />
        )}
      </Box>
    </Card>
  );
};

export { TopTeamsByHoursChart };
